import type { Slide } from '@dreamstack/feature-components/src/keenSlider/AccentroSlider'
import { AccentroSlider } from '@dreamstack/feature-components/src/keenSlider/AccentroSlider'
import { transformPropstackImageUrl } from '@dreamstack/feature-components/src/property/propstack-image-url-transform'
import { assertPopulatedDeep } from '@dreamstack/payload/src/lib/assertPopulated'
import type {
  BlockImageGallery as BlockImageGalleryType,
  Media,
} from '@dreamstack/payload/src/payload-types'
import { cn } from '@dreamstack/util/src/cn'
import map from 'lodash/map'
import type { FunctionComponent } from 'react'

type ImageGallerySize = 'Big' | 'Small'

type GalleryImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  size?: ImageGallerySize
}

const GalleryImage: React.FC<GalleryImageProps> = ({
  size,
  className,
  alt = '',
  ...rest
}) => (
  // Using native img intentionally inside slider (controlled sizing); Next/Image not ideal here
  // eslint-disable-next-line @next/next/no-img-element
  <img
    className={cn('w-full object-cover bg-transparent', className)}
    alt={alt}
    {...rest}
  />
)

type ImageWrapperProps = React.HTMLAttributes<HTMLDivElement> & {
  size?: ImageGallerySize
}

const ImageWrapper: React.FC<ImageWrapperProps> = ({
  size,
  className,
  ...rest
}) => (
  <div
    className={cn(
      'h-[190px] aspect-[3/2] relative',
      size === 'Big' && 'md:h-[523px] ml:h-[702px]',
      size === 'Small' && 'md:h-[254px]',
      className
    )}
    {...rest}
  />
)

// ImageGallerySize moved above for reuse

export const BlockImageGallery: FunctionComponent<
  React.PropsWithChildren<{
    block: BlockImageGalleryType
    images?: Pick<Media, 'title' | 'url'>[]
  }>
> = ({ block }) => {
  assertPopulatedDeep(block)
  const size = block.size
  const imageSlides = map(block.images, (imgItem, index) => {
    const img = imgItem?.image
    const url = transformPropstackImageUrl({ url: img?.url, size: 'default' })
    return {
      title: img?.title,
      slide: (
        <ImageWrapper size={size ?? undefined} key={index}>
          <GalleryImage size={size ?? undefined} src={url ?? ''} />
        </ImageWrapper>
      ),
    } as Slide
  })
  return (
    <div className="relative col-span-12 ml:col-start-2 ml:col-span-11">
      <AccentroSlider
        optionType={
          size === 'Big' ? 'BlockImageGalleryBig' : 'BlockImageGallerySmall'
        }
        slides={imageSlides}
        useBottomNavigation
      />
    </div>
  )
}
